import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { Reveal, MaskLines } from "./Reveal";

export default function ServicePage({ data }) {
  const lenis = useLenis();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
  }, [lenis, data.slug]);

  const toConnect = () => navigate("/#connect");

  return (
    <div className="bg-ink" data-testid={`service-page-${data.slug}`}>
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative h-[82svh] min-h-[560px] w-full overflow-hidden" data-testid="service-hero">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={data.heroImg} alt={data.eyebrow} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/30" />
        </motion.div>

        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="overline mb-7"
          >
            {data.eyebrow} · Title & Escrow
          </motion.div>
          <MaskLines
            lines={[data.title]}
            delay={0.6}
            testid="service-hero-title"
            className="font-serif font-light text-ivory leading-[1.04] text-[clamp(2.25rem,5vw,4.75rem)] tracking-tight max-w-4xl"
          />
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-8 max-w-xl text-taupe text-base md:text-lg font-light leading-relaxed"
          >
            {data.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-11 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <button
              onClick={toConnect}
              data-testid="service-hero-cta"
              className="group relative overflow-hidden bg-ivory text-ink text-[0.72rem] uppercase tracking-[0.2em] px-9 py-4"
            >
              <span className="relative z-10">Be Notified</span>
              <span className="absolute inset-0 bg-gold translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
            </button>
            <button
              onClick={() => lenis?.scrollTo("#service-offerings", { offset: -20, duration: 1.4 })}
              className="group text-ivory text-[0.72rem] uppercase tracking-[0.2em] px-2 py-4 border-b border-gold/50 hover:border-gold transition-colors duration-300"
            >
              Explore Services
              <span className="ml-3 inline-block transition-transform duration-300 group-hover:translate-x-1 text-gold">→</span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="relative bg-ink py-28 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative overflow-hidden aspect-[4/5] max-w-md">
                  <img src={data.introImg} alt={data.introHeading} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal><div className="overline mb-6">{data.eyebrow}</div></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-ivory text-4xl md:text-5xl leading-tight mb-8">
                  {data.introHeading}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-taupe text-base md:text-lg font-light leading-relaxed max-w-xl">
                  {data.introBody}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Perspectives (optional, e.g. residential) */}
      {data.perspectives && (
        <section className="relative bg-ink py-28 md:py-36 border-t border-charcoal/40" data-testid="service-perspectives">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <div className="max-w-2xl mb-16">
              <Reveal><div className="overline mb-6">Every Perspective</div></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-ivory text-4xl md:text-6xl tracking-tight leading-[1.02]">
                  {data.perspectives.heading}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 text-taupe text-base md:text-lg font-light leading-relaxed">
                  {data.perspectives.body}
                </p>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.perspectives.items.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Reveal key={p.label} delay={i * 0.1}>
                    <motion.div
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="group relative h-full bg-card border border-charcoal/60 p-9 md:p-10 overflow-hidden"
                      data-testid={`perspective-${i}`}
                    >
                      <Icon size={26} strokeWidth={1.2} className="text-gold mb-7" />
                      <div className="overline text-[0.6rem] text-taupe mb-4">{p.label}</div>
                      <h3 className="font-serif text-2xl md:text-3xl text-ivory font-light mb-4">{p.title}</h3>
                      <p className="text-taupe text-sm font-light leading-relaxed">{p.desc}</p>
                      <span className="absolute bottom-0 left-0 h-px w-0 bg-gold transition-all duration-700 group-hover:w-full" />
                    </motion.div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Offerings */}
      <section id="service-offerings" className="relative bg-ink py-28 md:py-36 border-t border-charcoal/40" data-testid="service-offerings">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><div className="overline mb-6">What We Handle</div></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-ivory text-4xl md:text-6xl tracking-tight mb-16 max-w-2xl">
              A complete {data.eyebrow.toLowerCase()} closing service.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.offerings.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal key={o.title} delay={(i % 2) * 0.1}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="group relative h-full bg-card border border-charcoal/60 p-9 md:p-10 overflow-hidden"
                    data-testid={`offering-${i}`}
                  >
                    <Icon size={26} strokeWidth={1.2} className="text-gold mb-7" />
                    <h3 className="font-serif text-2xl md:text-3xl text-ivory font-light mb-3">{o.title}</h3>
                    <p className="text-taupe text-sm font-light leading-relaxed max-w-md">{o.desc}</p>
                    <span className="absolute bottom-0 left-0 h-px w-0 bg-gold transition-all duration-700 group-hover:w-full" />
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Points */}
      <section className="relative bg-ink py-28 md:py-36 border-t border-charcoal/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
              <Reveal><div className="overline mb-6">The Difference</div></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-ivory text-4xl md:text-5xl leading-tight tracking-tight">
                  Why {data.eyebrow.toLowerCase()} clients choose us.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              {data.points.map((p, i) => (
                <Reveal key={p} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ x: 8 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center gap-8 py-7 border-b border-charcoal/50"
                  >
                    <span className="font-serif text-gold/70 text-lg w-10">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-2xl md:text-3xl text-ivory font-light">{p}</span>
                  </motion.div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative bg-ink py-28 md:py-36 border-t border-charcoal/40">
        <img
          src={`${process.env.PUBLIC_URL || ""}/htc-emblem.png`}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 opacity-[0.05]"
        />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 text-center">
          <Reveal><div className="overline mb-8 justify-center">Now Accepting Early Partnership Requests</div></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-ivory text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
              Preparing for a {data.eyebrow.toLowerCase()} transaction?
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl mx-auto text-taupe font-light leading-relaxed">
              We're completing licensing and would love to connect ahead of launch. Introduce yourself and
              we'll be in touch personally.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <button
              onClick={toConnect}
              data-testid="service-cta-connect"
              className="group relative mt-12 overflow-hidden bg-ivory text-ink text-[0.72rem] uppercase tracking-[0.2em] px-10 py-4"
            >
              <span className="relative z-10">Request an Introduction</span>
              <span className="absolute inset-0 bg-gold translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
            </button>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
